import { PowerBiReport, ViewModel, EmbedTokenResult, ActivityLogEntry, User, ExportFileRequest } from '../models/models';
import { isExpired, decodeToken } from "react-jwt";

const logHeader = "[DashboardWebApi]";

export type LoginStatus = "Pending" | "Success" | "Failed";

export default class DashboardWebApi {
  // Note: The web api port needs to set to :44302 for development and to
  // an empty string for production
  private static readonly baseUrl = `${globalThis.location.protocol}//${globalThis.location.hostname}`;
  public static readonly ApiRoot: string = globalThis.location.hostname === "localhost"
    ? `${this.baseUrl}:44302/api/`
    : `${this.baseUrl}/api/`;
  public static readonly SamlRoot: string = globalThis.location.hostname === "localhost"
    ? `${this.baseUrl}:44302/saml/`
    : `${this.baseUrl}/saml/`;

  public static GetAccessToken(): string {
    const accessToken = sessionStorage.getItem("accessToken");
    if (accessToken === null || accessToken === "") return "";

    const expired = isExpired(accessToken);
    if (expired) {
      console.debug(`${logHeader} Local access token  has expired`);
      this.ResetAuthentication();
      return "";
    }
    return accessToken;
  }

  /**
   * Validates that a URL is an internal report URL to prevent open redirect attacks.
   * Only allows relative URLs starting with /reports/
   */
  private static IsValidInternalReportUrl(url: string): boolean {
    if (!url || typeof url !== 'string') {
      return false;
    }

    // Must start with /reports/ (relative URL)
    if (!url.startsWith('/reports/')) {
      return false;
    }

    // Must not contain protocol (prevents absolute URLs like http://evil.com)
    if (url.includes('://')) {
      return false;
    }

    // Must not contain backslashes (Windows path traversal)
    if (url.includes('\\')) {
      return false;
    }

    return true;
  }

  /**
   * Sanitizes filename from Content-Disposition header to prevent XSS attacks.
   * Removes path separators and special characters.
   */
  private static SanitizeFilename(filename: string): string {
    if (!filename || typeof filename !== 'string') {
      return 'download';
    }

    // Remove quotes that may wrap the filename
    let sanitized = filename.replaceAll(/(^["'])|(["']$)/g, '');

    // Only keep safe characters: alphanumeric, spaces, dots, hyphens, underscores, and parentheses
    // This removes control characters, path separators, and dangerous characters in one go
    sanitized = sanitized.replaceAll(/[^a-zA-Z0-9.\-_ ()]+/g, '');

    // If nothing left, use default
    if (!sanitized || sanitized.trim() === '') {
      return 'download';
    }

    return sanitized;
  }

  static readonly GetAuthorization = async (): Promise<string> => {
    const restUrl = `${this.ApiRoot}authorization`;

    const response = await fetch(restUrl, {
      method: "GET",
      credentials: 'include',
      headers: {
        "Accept": "application/text;"
      }
    });

    console.debug(`${logHeader} Authorization response status code = %d`,
                  response.status);

    if (response.ok) {
      const stringResponse = await response.text();
      console.debug(`${logHeader} Authorization response = `,
                    stringResponse);
      return stringResponse;
    } else {
      return null;
    }
  }

  static readonly GetRenewedJWT = async (): Promise<string> => {
    const accessToken = this.GetAccessToken();
    if (accessToken === "") return null;
    console.debug(`${logHeader} Renewing access token`);
   
    const restUrl = `${this.ApiRoot}RenewJWT/`;

    const response = await fetch(restUrl, {
      method: "GET",
      headers: {
        "Accept": "application/text;",
        "Authorization": `Bearer ${accessToken}`
      }
    });

    console.debug(`${logHeader} Renew access token response status code = `,
                  response.status);

    if (response.ok) {
      const stringResponse = await response.text();
      sessionStorage.setItem("accessToken", stringResponse);
      return stringResponse;
    } else {
      return null;
    }
  }

  static async validateDefaultReportUrl(defaultreport: string) {
    if (this.IsValidInternalReportUrl(defaultreport)) {
      // Remove "/reports/" prefix (first occurrence only)
      const reportId = defaultreport.replace("/reports/", "");
      const result = await this.ValidateReport(reportId);
      if (result) {
        // deepcode ignore OR: URL validated with IsValidInternalReportUrl() - only allows internal /reports/ paths
        globalThis.location.href = defaultreport
      } else {
        globalThis.location.href = "/"
        localStorage.removeItem("defaultreport");
      }
    } else {
      // Invalid URL format - remove and redirect to home
      console.warn(`${logHeader} Invalid default report URL format, redirecting to home`);
      localStorage.removeItem("defaultreport");
      globalThis.location.href = "/"
    }
  }

  static readonly LoginUser = async (accessToken:string): Promise<number> => {
    console.debug(`${logHeader} Invoking user login`);
    const restUrl = `${this.ApiRoot}UserLogin/validate`;

    const response = await fetch(restUrl, {
      method: "GET",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
        "Authorization": `Bearer ${accessToken}`
      }
    });

    if (response.ok) {
      console.debug(`${logHeader} Succesfully validated user`);
      sessionStorage.setItem("isAuthenticated", "true");
      sessionStorage.setItem("accessToken", accessToken);

      const decodedToken: any = decodeToken(accessToken);
      console.debug(`${logHeader} decodedToken`, {decodedToken});
      if (decodedToken?.exp) {
        const LoginId = decodedToken['sub'];
        const givenName = decodedToken['given_name'];
        const familyName = decodedToken['family_name'];
        const UserName = `${givenName} ${familyName}`;

        sessionStorage.setItem("LoginId", LoginId);
        sessionStorage.setItem("UserName", UserName)
      }

      const defaultreport = localStorage.getItem("defaultreport");
      console.debug(`${logHeader} Default report = `,
                    defaultreport);
      if (defaultreport) {
        // Validate URL to prevent open redirect attacks
        // Only allow relative URLs starting with /reports/
        await this.validateDefaultReportUrl(defaultreport);
      } else {
        globalThis.location.href = "/"
      }
    } else {
      console.debug(`${logHeader} Unable to validate user`);
      this.ResetAuthentication()
      globalThis.location.href = "/"
    }

    console.debug(
      `${logHeader} Setting loginUserStatus to ${response.status.toString()}`);
    sessionStorage.setItem("loginUserStatus", response.status === 204 ? "Success" : "Failed");
    
    return response.status;
  }

  static GetUser(): User {
    const user = new User();
    const LoginId = sessionStorage.getItem("LoginId");
    const UserName = sessionStorage.getItem("UserName");

    if (LoginId && UserName) {
      user.LoginId = LoginId;
      user.UserName = UserName;
    }

    return user;
  }

  static readonly GetEmbeddingData = async (): Promise<ViewModel> => {
    const accessToken = this.GetAccessToken();
    if (accessToken === "") return null;

    const restUrl = `${this.ApiRoot}Embed/`;

    return fetch(restUrl, {
       method: "GET",
       headers: {
         "Accept": "application/json;",
         "Authorization": `Bearer ${accessToken}`
      }
    }).then(response => response.json())
      .then(response => response);
  }

  static readonly GetEmbedToken = async (): Promise<EmbedTokenResult> => {
    const accessToken = this.GetAccessToken();
    if (accessToken === "") return null;
    console.debug(`${logHeader} Getting embed token`);

    const restUrl = `${DashboardWebApi.ApiRoot}EmbedToken/`;

    return fetch(restUrl, {
      method: "GET",
      headers: {
        "Accept": "application/json;",
        "Authorization": `Bearer ${accessToken}`
      }
    }).then(response => response.json())
      .then(response => response);
  }

  static readonly RefreshDataset = async (reportId: string): Promise<string> => {
    const accessToken = this.GetAccessToken();
    if (accessToken === "") return "Missing access token";

    const report = new PowerBiReport();
    report.id = reportId;
    const postData: string = JSON.stringify(report);
    const restUrl = `${this.ApiRoot}RefreshDataset/`;

    const response = await fetch(restUrl, {
      method: "POST",
      body: postData,
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
        "Authorization": `Bearer ${accessToken}`
      }
    });

    return await response.text();
  }

  static readonly GetLastRefreshTime = async (reportId: string): Promise<number> => {
    const accessToken = this.GetAccessToken();
    if (accessToken === "") return 0;

    const restUrl = `${this.ApiRoot}RefreshDataSet/lastRefreshTime?report=${reportId}`;

    const response = await fetch(restUrl, {
      method: "GET",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
        "Authorization": `Bearer ${accessToken}`
      }
    });

    return Number(await response.text());
  }

  static readonly LogActivity = async (activityLogEntry: ActivityLogEntry): Promise<void> => {
    document.dispatchEvent(new Event("mousedown"));

    const accessToken = this.GetAccessToken();
    if (accessToken === "") return;

    const postData: string = JSON.stringify(activityLogEntry);
    const restUrl = `${this.ApiRoot}ActivityLog/`;

    const response = await fetch(restUrl, {
      method: "POST",
      body: postData,
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
        "Authorization": `Bearer ${accessToken}`
      }
    });

    if (response.ok) {
      await response.text();
    }
  }

  static readonly ExportFile = async (ExportRequest: ExportFileRequest): Promise<void> => {
    const accessToken = this.GetAccessToken();
    if (accessToken === "") return;

    console.debug(`${logHeader} Browser language = `, ExportRequest.Locale);
    const restUrl: string = `${this.ApiRoot}ExportFile/`;
    // prepare JSON body for POST request to retrieve exported report file
    const postData: string = JSON.stringify(ExportRequest);

    // execute POST request synchronously to retrieve exported report file
    const fetchResponse = await fetch(restUrl, {
      method: "POST",
      body: postData,
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
        "Authorization": `Bearer ${accessToken}`
      }
    });

    // Once POST call returns, get file name from HTTP response
    const header = fetchResponse.headers.get('Content-Disposition');
    console.debug(`${logHeader} Content-Disposition = `, header);
    const parts = header.split(';');
    const rawFilename = parts[1].split('=')[1];

    // Sanitize filename to prevent XSS attacks
    const filename = this.SanitizeFilename(rawFilename);
    console.debug(`${logHeader} Sanitized filename = `, filename);

    // get blob with export file content
    const blob = await fetchResponse.blob();

    // trigger export file download in browser window
    const url = globalThis.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    // deepcode ignore DOMXSS: Filename sanitized with SanitizeFilename() - removes all dangerous characters
    document.body.appendChild(a);
    a.click();
    a.remove();

    // return control to caller using await
  }

  static readonly ValidateReport = async (reportId: string): Promise<boolean> => {
    console.debug(`${logHeader} Validating report %s`, reportId);
    const accessToken = this.GetAccessToken();
    if (accessToken === "") return false;

    const report = new PowerBiReport();
    report.id = reportId;
    const postData: string = JSON.stringify(report);
    const restUrl = `${this.ApiRoot}ValidateReport/`;

    const response = await fetch(restUrl, {
      method: "POST",
      body: postData,
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
        "Authorization": `Bearer ${accessToken}`
      }
    });

    if (!response.ok) {
      console.debug(`${logHeader} Report %s is not valid`, reportId);
      return false
    }

    console.debug(`${logHeader} Report %s is valid`, reportId);
    return true;
  }

  public static IsAuthenticated(): boolean {
    return Boolean(sessionStorage.getItem("isAuthenticated"));
  }
  
  public static ResetAuthentication() {
    sessionStorage.setItem("isAuthenticated", "false");
    sessionStorage.setItem("accessToken", "");
    sessionStorage.setItem("LoginId", "");
    sessionStorage.setItem("UserName", "");
  }

  public static ResetLoginStatus() {
    sessionStorage.setItem("loginUserStatus", "Pending");
  }

  public static GetLoginStatus(): LoginStatus {
    const status = sessionStorage.getItem("loginUserStatus");
    if (status === null || status === "Pending") {
      return "Pending";
    } else if (status == "Success") {
      return "Success";
    }
    return "Failed"; 
  }
}

